import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { UserContext } from '../context/UserContext';
import { canModify, canAdminDelete } from '../utils/permissions';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Link, useNavigate } from 'react-router-dom';
import { useOpportunityStore, type Opportunity } from '../stores/opportunityStore';
import { useStageStore } from '../stores/stageStore';
import { updateOpportunity, getOpportunities } from '../services/api';
import { ConfirmModal, Amount, MoreMenu, btnPrimary } from '@redpanda/ui';
import { useTranslation } from 'react-i18next';
import OpportunityDialog from '../components/OpportunityDialog';
import SalesFunnelChart from '../components/SalesFunnelChart';
import { useRememberedFlag } from '@redpanda/utils';
import { rememberOpportunityView } from '../utils/opportunityView';
import { useOpportunityBoardStore } from '../stores/opportunityBoardStore';
import { OpportunityViewTabs, OpportunityMoreMenu } from '../components/OpportunityViewControls';
import { toast } from 'react-hot-toast';
import { notifyDataChanged, useDataChanged } from '../utils/dataEvents';

// 看板一次載入的商機上限（列表 store 每頁只有 20 筆，看板要看到全部）
const KANBAN_LIMIT = 1000;

const OpportunityKanbanPage: React.FC = () => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const userRole = userContext?.user?.role;
  const currentUserId = userContext?.user?.id;
  const navigate = useNavigate();
  const {
    opportunities,
    itemToDeleteId,
    updateOpportunity: updateOpportunityInStore,
    handleDelete,
    confirmDelete,
    showDeleteConfirmModal,
    closeDeleteModal,
    isDeleting,
  } = useOpportunityStore();

  const {
    stages,
    loading: stagesLoading,
    fetchStages,
  } = useStageStore();
  const [dragLoading, setDragLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState<Opportunity | null>(null);
  const [showFunnelChart, setShowFunnelChart] = useRememberedFlag('crm-ui:opportunity.funnel', false);
  useEffect(() => rememberOpportunityView('kanban'), []); // 側欄「商機」下次直接開看板

  // 看板資料：另外抓全部商機（商機 store 只有目前那一頁，拿它分組會漏掉第 2 頁以後的商機）
  // 先顯示上次的看板（persist），背景重抓；從沒抓過時先用列表那一頁
  // boardTotal：後端回報的商機總數，超過看板上限時顯示提示（同 Jira／HubSpot：看板只載入前 N 筆，請用列表篩選）
  const { items: savedBoard, total: boardTotal, setBoard, updateItems: setBoardItems } = useOpportunityBoardStore();
  const boardItems = savedBoard ?? opportunities;
  const loadBoard = useCallback(async () => {
    try {
      const response = await getOpportunities(1, { pageSize: KANBAN_LIMIT });
      setBoard(response.data?.data || [], response.data?.pagination?.totalItems ?? 0);
    } catch (error) {
      console.error('載入商機失敗:', error);
      toast.error(t('opportunities.kanban.loadFailed', { ns: 'pages' }));
    }
  }, [setBoard, t]);

  // 新增／編輯／刪除在 API 成功後才會通知（對話框是樂觀更新，關閉時資料還沒寫進後端），收到再重抓
  // 拖曳換階段時看板已用伺服器回傳資料更新，只需要漏斗、統計重抓：通知期間略過看板重抓（事件是同步派送）
  const skipBoardReloadRef = useRef(false);
  useDataChanged('opportunities', () => {
    if (!skipBoardReloadRef.current) loadBoard();
  });

  // 組織商機資料按階段分組
  const opportunitiesByStage = React.useMemo(() => {
    const grouped: Record<string, Opportunity[]> = {};
    
    // 初始化每個階段的空陣列
    stages.forEach(stage => {
      grouped[stage.id] = [];
    });
    
    // 將商機分組到對應階段
    boardItems.forEach(opportunity => {
      if (grouped[opportunity.currentStageId]) {
        grouped[opportunity.currentStageId].push(opportunity);
      }
    });
    
    return grouped;
  }, [boardItems, stages]);

  // 載入階段和商機資料
  useEffect(() => {
    const loadData = async () => {
      try {
        // 並行載入階段和商機資料，使用快取
        await fetchStages(); // 使用快取的階段載入
      } catch (error) {
        console.error('載入資料失敗:', error);
        toast.error(t('opportunities.kanban.loadFailed', { ns: 'pages' }));
      }
    };

    loadData();
    loadBoard();
  }, [fetchStages, loadBoard, t]);

  // 處理拖拉結束事件
  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;

    // 如果沒有目的地，表示拖拉取消
    if (!destination) {
      return;
    }

    // 如果位置沒有改變，不需要處理
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const opportunityId = draggableId;
    const newStageId = destination.droppableId;
    
    // 找到被移動的商機
    const opportunity = boardItems.find(opp => opp.id === opportunityId);
    if (!opportunity) {
      toast.error(t('opportunities.kanban.opportunityNotFound', { ns: 'pages' }));
      return;
    }

    try {
      setDragLoading(true);
      
      // 先做樂觀更新，讓 UI 立即反映變化
      const optimisticUpdate = {
        ...opportunity,
        currentStageId: newStageId,
      };
      setBoardItems(prev => prev.map(opp => (opp.id === opportunityId ? optimisticUpdate : opp)));
      updateOpportunityInStore(opportunityId, optimisticUpdate);
      
      // 然後呼叫 API 更新商機階段
      const response = await updateOpportunity(opportunityId, {
        currentStageId: newStageId,
      });

      if (response.data) {
        // API 成功後，用完整的伺服器資料覆蓋樂觀更新
        setBoardItems(prev => prev.map(opp => (opp.id === opportunityId ? response.data : opp)));
        updateOpportunityInStore(opportunityId, response.data);
        
        const stageName = stages.find(stage => stage.id === newStageId)?.name;
        skipBoardReloadRef.current = true;
        notifyDataChanged('opportunities'); // 換階段後漏斗、統計跟著更新
        skipBoardReloadRef.current = false;
        toast.success(t('opportunities.kanban.moveSuccess', { title: opportunity.title, stage: stageName, ns: 'pages' }));
      }
    } catch (error) {
      console.error('更新商機階段失敗:', error);
      toast.error(t('opportunities.kanban.updateFailed', { ns: 'pages' }));
      
      // 發生錯誤時，恢復原來的狀態
      setBoardItems(prev => prev.map(opp => (opp.id === opportunityId ? opportunity : opp)));
      updateOpportunityInStore(opportunityId, opportunity);
    } finally {
      setDragLoading(false);
    }
  };

  // 欄名前的小色點：成交＝完成、機率 0＝停用、其餘＝進行中（§3-10 不用飽和色實心欄頭）
  const stageDotClass = (probability: number) =>
    probability >= 1 ? 'bg-green-500' : probability <= 0 ? 'bg-gray-400' : 'bg-accent';

  // 計算階段統計
  const getStageStats = (stageId: string) => {
    const stageOpportunities = opportunitiesByStage[stageId] || [];
    const totalValue = stageOpportunities.reduce((sum, opp) => sum + opp.value, 0);
    return {
      count: stageOpportunities.length,
      totalValue,
    };
  };

  // 處理編輯商機
  const handleEditOpportunity = (opportunity: Opportunity) => {
    setEditingOpportunity(opportunity);
    setShowEditDialog(true);
  };

  // 關閉對話框
  const handleCloseDialogs = () => {
    setShowAddDialog(false);
    setShowEditDialog(false);
    setEditingOpportunity(null);
  };

  // 刪除：看板先移除該卡；store 刪除完成後會通知商機異動，看板重新同步（刪除失敗時卡片會回來）
  const handleConfirmDelete = async () => {
    const deletingId = itemToDeleteId;
    if (deletingId) setBoardItems(prev => prev.filter(opp => opp.id !== deletingId));
    await confirmDelete();
  };


  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('opportunities.kanban.title', { ns: 'pages' })}</h1>
          <div className="flex space-x-3">
            {/* 新增商機（頁首唯一主要按鈕） */}
            <button onClick={() => setShowAddDialog(true)} className={btnPrimary}>
              {t('opportunities.list.addOpportunity', { ns: 'pages' })}
            </button>

            {/* 不常用的管理功能收進「⋯」；放最後：items-baseline 取右側第一個元件的文字基線，只有圖示的「⋯」放第一個會把標題往下推 */}
            <OpportunityMoreMenu />
          </div>
        </div>
        <OpportunityViewTabs current="kanban" showFunnel={showFunnelChart} onToggleFunnel={() => setShowFunnelChart(!showFunnelChart)} />
      </div>

      {/* 銷售漏斗圖表 */}
      {showFunnelChart && (
        <div className="mb-8">
          <SalesFunnelChart />
        </div>
      )}

      {boardTotal > boardItems.length && (
        <div className="mb-4 rounded-lg border border-line bg-surface px-4 py-3 text-sm text-fg-secondary">
          {t('opportunities.kanban.truncatedNotice', { ns: 'pages', shown: boardItems.length, total: boardTotal })}{' '}
          <Link to="/opportunities" className="font-medium text-accent hover:underline">
            {t('opportunities.kanban.openList', { ns: 'pages' })}
          </Link>
        </div>
      )}

      {stagesLoading ? (
        <div className="flex justify-center items-center h-64 bg-surface border border-line rounded-lg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
            <p className="mt-4 text-fg-secondary">{t('opportunities.kanban.loading', { ns: 'pages' })}</p>
          </div>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-6">
            {stages.map((stage) => {
            const stats = getStageStats(stage.id);
            
            return (
              <div key={stage.id} className="flex-shrink-0 w-72">
                <div className="rounded-lg bg-gray-100/70 dark:bg-slate-800/50">
                  {/* 欄頭：色點＋欄名＋數量；下一行金額小計與成交率（§3-10） */}
                  <div className="px-3 pt-3 pb-2">
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${stageDotClass(stage.probability)}`} aria-hidden="true" />
                      <h3 className="text-sm font-semibold text-fg truncate">{stage.name}</h3>
                      <span className="text-[13px] text-fg-secondary tabular-nums">{stats.count}</span>
                    </div>
                    <p className="mt-0.5 pl-4 text-[13px] text-fg-secondary tabular-nums">
                      <Amount value={stats.totalValue} />
                      <span className="mx-1.5 text-fg-muted">·</span>
                      {t('opportunities.kanban.conversionRate', { ns: 'pages' })} {Math.round(stage.probability * 100)}%
                    </p>
                  </div>

                  {/* 商機列表 */}
                  <Droppable droppableId={stage.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`px-2 pb-2 min-h-[200px] rounded-b-lg transition-colors ${
                          snapshot.isDraggingOver ? 'bg-accent-soft' : ''
                        }`}
                      >
                        {(opportunitiesByStage[stage.id] || []).map((opportunity, index) => (
                          <Draggable
                            key={opportunity.id}
                            draggableId={opportunity.id}
                            index={index}
                            isDragDisabled={dragLoading || !canModify(opportunity, userRole, currentUserId)}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`bg-surface rounded-md p-3 mb-2 border border-line cursor-move transition-shadow ${
                                  snapshot.isDragging ? 'shadow-lg ring-1 ring-accent' : 'hover:shadow-sm'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <Link
                                      to={`/opportunities/${opportunity.id}`}
                                      className="block text-fg hover:text-accent transition-colors"
                                    >
                                      <h4 className="text-sm font-medium line-clamp-2">
                                        {opportunity.title}
                                      </h4>
                                    </Link>
                                    <p className="text-[13px] text-fg-secondary mt-0.5 truncate">
                                      {opportunity.customer.company || opportunity.customer.name}
                                    </p>
                                  </div>
                                  <MoreMenu
                                    variant="icon"
                                    className="ml-2 flex-shrink-0"
                                    label={t('common.table.columns.actions')}
                                    items={[
                                      { key: 'view', label: t('opportunities.kanban.viewDetail', { ns: 'pages' }), onClick: () => navigate(`/opportunities/${opportunity.id}`) },
                                      // 權限與列表相同：編輯看建立者，刪除只開放管理員（utils/permissions.ts）
                                      ...(canModify(opportunity, userRole, currentUserId)
                                        ? [{ key: 'edit', label: t('common.actions.edit'), onClick: () => handleEditOpportunity(opportunity) }]
                                        : []),
                                      ...(canAdminDelete(opportunity, userRole)
                                        ? [{ key: 'delete', label: t('common.actions.delete'), tone: 'danger' as const, onClick: () => handleDelete(opportunity.id) }]
                                        : []),
                                    ]}
                                  />
                                </div>

                                {/* 底部一行：金額＋負責人；描述不放卡片（§3-10） */}
                                <div className="mt-2 flex items-center justify-between gap-2">
                                  <span className="text-sm font-medium text-fg tabular-nums">
                                    <Amount value={opportunity.value} />
                                  </span>
                                  <span className="text-[13px] text-fg-secondary truncate">
                                    {opportunity.createdByName}
                                  </span>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        
                        {/* 空狀態提示 */}
                        {(opportunitiesByStage[stage.id] || []).length === 0 && (
                          <p className="py-8 text-center text-[13px] text-fg-muted">
                            {t('opportunities.kanban.dropHere', { ns: 'pages' })}
                          </p>
                        )}
                      </div>
                    )}
                  </Droppable>
                </div>
              </div>
            );
            })}
          </div>
        </DragDropContext>
      )}

      {/* 刪除確認對話框 */}
      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title={t('opportunities.list.confirmDeleteTitle', { ns: 'pages' })}
        message={t('opportunities.list.confirmDeleteMessage', { ns: 'pages' })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

        {/* 新增商機對話框 */}
        <OpportunityDialog
          isOpen={showAddDialog}
          onClose={handleCloseDialogs}
        />

        {/* 編輯商機對話框 */}
        <OpportunityDialog
          isOpen={showEditDialog}
          onClose={handleCloseDialogs}
          opportunity={editingOpportunity}
        />
    </div>
  );
};

export default OpportunityKanbanPage;